import React from 'react'
import clsx from 'clsx'
import { useAppDispatch } from '../../../../../hooks/redux'
import { ButtonProps, Fade, Menu, MenuItem, useTheme } from '@mui/material'
import { CloseCircle, ArrowLeft } from 'iconsax-react'

import BaseModal from '../../../../molecules/ChatModals/BaseModal2'
import MediaAvatar from '../../../../atoms/MediaAvatar'
import Radio from '../../../../atoms/Radio'
import Button from '../../../../atoms/Button2'
import Result from './Result'
import ErrorToast from '../../../../molecules/ErrorToast'
import ButtonSecondary from '../../../../atoms/ButtonSecondary'

import { uniqueArray } from '../../../../../utilities'
import { addParticipant, removeParticipant } from '../../../../../lib/api'
import { addUsers, removeUsers } from '../../../../../slices/convo'
import { useBreakpoint } from '../../../../../providers/BreakpointProvider'
import { useQueueState } from '../../../../../providers/QueueStateProvider'
import { useEnvironment } from '../../../../../providers/EnvironmentProvider'
import { useUserAPI } from '../../../../../providers/UserAPIProvider'
import { AddSuccessAlert } from '../../../../../slices/alert'
import useDelayedUserSearch, {
  EXCLUDE_ME
} from '../../../../../hooks/useDelayedUserSearch/v2'
import useStyles from './styles'

type GreyButtonProps = ButtonProps & { mobile?: boolean }

export const GreyButton: React.FC<GreyButtonProps> = ({
  className,
  mobile,
  ...props
}) => {
  const classes = useStyles({})

  return (
    <ButtonSecondary
      {...props}
      classes={{
        root: clsx(
          mobile ? classes.buttonGreyMobileRoot : classes.buttonGreyRoot,
          className
        ),
        text: classes.buttonGreyLabel
      }}
    />
  )
}

type SubmodalDataType = {
  title: React.ReactNode
  text: React.ReactNode
  cancel: () => void
  confirm: () => void
  isRemove: boolean
}

const TOAST_DELAY = 3000
const SUBMODAL_DEFAULTS: SubmodalDataType = {
  title: '',
  text: '',
  cancel: () => {},
  confirm: () => {},
  isRemove: false
}

interface EditParticipantsModalProps {
  onClose: () => void
  mobile?: boolean
}

const EditParticipantsModal: React.FC<EditParticipantsModalProps> = ({
  onClose,
  mobile
}) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const { isMedium } = useBreakpoint()
  const { conversation } = useQueueState()
  const { user } = useEnvironment()
  const { allUsers, loadUser, updateUsers } = useUserAPI()

  const anchorRef = React.useRef<HTMLDivElement>()
  const inputRef = React.useRef<HTMLInputElement>()
  const [searchVal, setSearchVal] = React.useState('')
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [toAdd, setToAdd] = React.useState([])
  const [showSubModal, setShowSubModal] = React.useState(false)
  const [submodalData, setSubmodalData] = React.useState(SUBMODAL_DEFAULTS)
  const [error, setError] = React.useState(null)
  const currentUsers = React.useMemo(() => {
    const users = conversation.users ?? []
    const withoutMe = users.filter(id => id !== user?.id)
    return uniqueArray(withoutMe)
  }, [conversation.users, user])
  const addingNames = React.useMemo(() => {
    const names = []
    toAdd.forEach(id => {
      const user = allUsers[id]
      if (user) {
        names.push(user.name)
      }
    })

    if (names.length === 0) {
      return ''
    }

    if (names.length === 1) {
      return names[0]
    }

    if (names.length === 2) {
      return names.join(' and ')
    }

    names[names.length - 1] = `and ${names.at(-1)}`
    return names.join(', ')
  }, [toAdd, allUsers])

  const { search, results } = useDelayedUserSearch(
    false,
    EXCLUDE_ME(() => user?.id)
  )
  React.useEffect(() => {
    updateUsers(results)
  }, [updateUsers, results])

  const mainModalPaperProps = React.useMemo(() => {
    const common = {
      padding: '0',
      border: '1px solid #C6C6C6'
    }

    if (mobile) {
      return {
        style: {
          ...common,
          top: '0',
          transform: 'translateX(-50%)',
          borderRadius: '0',
          width: '100vw',
          maxWidth: 'unset',
          minHeight: 'unset',
          height: 'calc(100svh - 62.5px)',
          maxHeight: 'unset'
        }
      }
    }

    return {
      style: {
        ...common,
        borderRadius: '4px',
        width: isMedium ? 'calc(100% - 4rem)' : '100%',
        maxWidth: isMedium ? 'unset' : '480px'
      }
    }
  }, [mobile, isMedium])

  const subModalPaperProps = React.useMemo(() => {
    const common = {
      padding: '0',
      border: '1px solid #C6C6C6'
    }

    if (mobile) {
      return {
        style: {
          ...common,
          borderRadius: '7px',
          boxSizing: 'border-box',
          boxShadow: '0px 20px 30px rgba(0, 0, 0, 0.15)',
          width: '100%',
          maxWidth: 'calc(100% - 32px)',
          minHeight: 'unset',
          height: 'auto',
          maxHeight: 'unset'
        }
      }
    }

    return {
      style: {
        ...common,
        borderRadius: '4px',
        width: isMedium ? 'calc(100% - 4rem)' : '512px',
        maxWidth: 'unset'
      }
    }
  }, [mobile, isMedium])

  const classes = useStyles()

  // Show results when search is valid
  React.useEffect(() => {
    const showMenu = results.length > 0 && searchVal.length > 0
    setMenuOpen(showMenu)

    if (showMenu) {
      // Refocus input
      if (inputRef.current) {
        setTimeout(() => {
          inputRef.current.focus()
        }, 0)
      }
    }
  }, [results, searchVal])

  // Load any unloaded users
  React.useEffect(() => {
    const combined = uniqueArray(toAdd, currentUsers)

    combined.forEach(id => {
      if (!allUsers[id]) {
        loadUser(id)
      }
    })
  }, [toAdd, currentUsers, allUsers, loadUser])

  const showToast = React.useCallback(
    (message, isError = false) => {
      if (isError) {
        setError({ err: message })
        setTimeout(() => {
          setError(null)
        }, TOAST_DELAY)
        return
      }

      dispatch(AddSuccessAlert(message, TOAST_DELAY))
    },
    [dispatch]
  )

  const closeSubModal = React.useCallback(() => {
    setShowSubModal(false)
    setSubmodalData(SUBMODAL_DEFAULTS)
  }, [])

  const handleAddClick = React.useCallback(() => {
    const isSingular = toAdd.length === 1

    setSubmodalData({
      title: `Add Participant${isSingular ? '' : 's'}`,
      text: (
        <>
          Are you sure want to add <span>{addingNames}</span> to this
          conversation?
        </>
      ),
      cancel: closeSubModal,
      confirm: async () => {
        try {
          // Add to api
          await Promise.all(
            toAdd.map(async id => {
              await addParticipant(conversation.session, id)
            })
          )

          // Update conversation locally
          dispatch(addUsers({ id: conversation.session, users: toAdd }))

          setToAdd([])
          showToast(
            <>
              <span style={{ fontWeight: theme.bold.bold }}>{addingNames}</span>{' '}
              {isSingular ? 'has' : 'have'} been successfully added to this
              conversation.
            </>
          )
        } catch (err) {
          showToast(err, true)
        } finally {
          closeSubModal()
        }
      },
      isRemove: false
    })

    setShowSubModal(true)
  }, [
    toAdd,
    addingNames,
    closeSubModal,
    showToast,
    conversation,
    dispatch,
    theme
  ])

  const handleRemoveClick = React.useCallback(
    id => {
      const name = allUsers[id]?.name ?? ''

      setSubmodalData({
        title: 'Remove Participant',
        text: (
          <>
            Are you sure want to remove <span>{name}</span>?
          </>
        ),
        cancel: closeSubModal,
        confirm: async () => {
          try {
            // Remove from api
            await removeParticipant(conversation.session, id)

            // Update conversation locally
            dispatch(removeUsers({ id: conversation.session, users: [id] }))

            showToast(
              <>
                <span style={{ fontWeight: theme.bold.bold }}>{name}</span> has
                been removed from this conversation.
              </>
            )
          } catch (err) {
            showToast(err, true)
          } finally {
            closeSubModal()
          }
        },
        isRemove: true
      })

      setShowSubModal(true)
    },
    [allUsers, closeSubModal, conversation.session, dispatch, showToast, theme]
  )

  return (
    <>
      {/* @ts-ignore */}
      <BaseModal
        open
        hideTitle={mobile}
        showX={!mobile}
        title='Add/Remove Participant'
        onClose={onClose}
        PaperProps2={mainModalPaperProps}
        classes={{ title: classes.modalTitle, close: classes.modalClose }}
        childrenClass={mobile && classes.mobileChildren}
      >
        <div className={clsx(classes.root, mobile && classes.mobileRoot)}>
          {mobile && (
            <div className={classes.mobileTitle}>
              <ArrowLeft
                variant='Linear'
                size={theme.size.xl}
                onClick={onClose}
                className={classes.mobileBackArrow}
              />

              <span>Add/Remove Participant</span>
            </div>
          )}

          <div
            className={classes.title}
            style={{
              marginBottom: '8px'
            }}
          >
            Current Participants
            {currentUsers.length > 1 ? ` (${currentUsers.length})` : ''}
          </div>

          <div className={classes.participants}>
            {currentUsers.map(id => {
              const disabled = currentUsers.length === 1
              const user = allUsers[id]

              return (
                <div key={id} className={classes.participant}>
                  {/* @ts-ignore */}
                  <MediaAvatar
                    user={user}
                    className={classes.participantAvatar}
                  />

                  <div className={classes.participantName}>{user?.name}</div>

                  <div
                    onClick={() => {
                      if (!disabled) {
                        handleRemoveClick(user.id)
                      }
                    }}
                    className={clsx(
                      classes.participantRemove,
                      disabled && classes.participantRemoveDisabled
                    )}
                  >
                    Remove
                  </div>
                </div>
              )
            })}
          </div>

          <div
            className={classes.title}
            style={{
              marginBottom: '16px'
            }}
          >
            Add New Participant
          </div>

          <div className={classes.radio}>
            <div className={classes.radioButton}>
              <Radio checked />

              <span>Users</span>
            </div>
          </div>

          <div
            ref={anchorRef}
            onClick={() => {
              if (inputRef.current) {
                inputRef.current.focus()
              }
            }}
            className={classes.inputArea}
          >
            {toAdd.map(id => {
              const user = allUsers[id]

              return (
                <div
                  key={id}
                  onClick={e => e.stopPropagation()}
                  className={classes.inputItem}
                >
                  {/* @ts-ignore */}
                  <MediaAvatar
                    user={user}
                    className={classes.inputItemAvatar}
                  />

                  <div className={classes.inputItemName}>{user?.name}</div>

                  <CloseCircle
                    size={theme.size.base}
                    onClick={() => {
                      setToAdd(old => old.filter(oid => oid !== id))
                    }}
                    className={classes.inputItemDelete}
                  />
                </div>
              )
            })}

            <input
              ref={inputRef}
              placeholder='Select User'
              value={searchVal}
              onChange={e => {
                // Update value and lookup users
                const val = e.target.value
                setSearchVal(val)
                search(val)
              }}
              onKeyDown={e => {
                if (
                  e.key === 'Backspace' &&
                  e.currentTarget.value.length === 0 &&
                  toAdd.length > 0
                ) {
                  e.preventDefault()

                  // Remove last id from list
                  const last = toAdd[toAdd.length - 1]
                  setToAdd(old => old.slice(0, -1))

                  // Set search value to last name
                  const name = allUsers[last]?.name ?? ''
                  setSearchVal(name)
                  search(name)
                }
              }}
              className={classes.input}
            />

            <Menu
              open={menuOpen}
              disableEnforceFocus
              anchorEl={anchorRef.current}
              anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
              transformOrigin={{ horizontal: 'center', vertical: 4 }}
              TransitionComponent={Fade}
              classes={{ paper: classes.resultsContainer }}
              PaperProps={{
                style: {
                  width: `${
                    anchorRef.current?.getBoundingClientRect()?.width ?? 0
                  }px`
                }
              }}
            >
              <div className={classes.resultsTitle}>Results</div>

              <div id='edit-search-results' className={classes.results}>
                {results.map(user => (
                  <MenuItem
                    key={user.id}
                    component='div'
                    onClick={() => {
                      // Add person to list
                      setToAdd(old => uniqueArray(old, user.id))

                      // Reset state
                      setSearchVal('')
                      setMenuOpen(false)
                      search('')
                    }}
                    className={classes.result}
                    disableGutters
                  >
                    <Result user={user} />
                  </MenuItem>
                ))}
              </div>
            </Menu>
          </div>

          {toAdd.length > 0 && (
            <>
              <div
                className={clsx(
                  classes.buttonArea,
                  mobile && classes.buttonAreaMobile
                )}
              >
                <Button
                  onClick={handleAddClick}
                  className={clsx(
                    classes.button,
                    mobile && classes.fullWidthButton
                  )}
                >
                  Add Participant{toAdd.length === 1 ? '' : 's'}
                </Button>
              </div>
            </>
          )}
        </div>
      </BaseModal>

      {showSubModal && (
        <BaseModal
          open
          hideTitle
          showX={!mobile}
          onClose={closeSubModal}
          PaperProps2={subModalPaperProps}
          // @ts-ignore
          classes={{ close: classes.modalClose }}
        >
          <div
            className={clsx(classes.root, mobile && classes.submodalMobileRoot)}
          >
            <div className={classes.submodalTitle}>{submodalData.title}</div>

            <div className={classes.submodalText}>{submodalData.text}</div>

            <div
              className={clsx(
                classes.buttonArea,
                classes.buttonAreaSub,
                mobile && classes.submodalButtonAreaMobile
              )}
            >
              {submodalData.isRemove ? (
                <>
                  <Button
                    onClick={submodalData.cancel}
                    className={classes.button}
                  >
                    Cancel
                  </Button>

                  <GreyButton
                    onClick={submodalData.confirm}
                    mobile={mobile}
                    className={classes.button}
                  >
                    Remove
                  </GreyButton>
                </>
              ) : (
                <>
                  <GreyButton
                    onClick={submodalData.cancel}
                    mobile={mobile}
                    className={classes.button}
                  >
                    Cancel
                  </GreyButton>

                  <Button
                    onClick={submodalData.confirm}
                    className={classes.button}
                  >
                    Yes
                  </Button>
                </>
              )}
            </div>
          </div>
        </BaseModal>
      )}

      {error && <ErrorToast error={error} />}
    </>
  )
}

export default EditParticipantsModal
